import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";

import { IDraftRecommendation } from "../../../store/queries/contractModelDrafts";
import { componentTypeLabel } from "../utils";
import { serviceLineOf, serviceLinesIn } from "./steps";

interface IProps {
  recs: IDraftRecommendation[];
  selected: string[];
  busy: boolean;
  onToggle: (line: string) => void;
  onApply: () => void;
}

/**
 * "Find the service lines, and let me pick which ones I actually want to model."
 *
 * Deselecting a line REJECTS its recommendations. That is not a new mechanism — a rejected rec already
 * drops off the implementation checklist — so skipping a service line simply means nobody is ever told
 * to build it. Nothing is deleted, and re-selecting a line restores it to `pending`.
 *
 * Recommendations with no service line (contract-wide terms, routing decisions, whole-contract rules)
 * are never filtered out here. They aren't "a service line" you can decline.
 */
function ServiceLinePicker({
  recs,
  selected,
  busy,
  onToggle,
  onApply,
}: IProps) {
  const lines = serviceLinesIn(recs);

  if (lines.length === 0) {
    return (
      <p className="text-700">
        The agent did not attribute any recommendation to a named service line,
        so there is nothing to pick from. Everything in this contract is modeled
        as contract-wide.
      </p>
    );
  }

  const recsFor = (line: string) =>
    recs.filter((r) => serviceLineOf(r) === line);
  const skipped = lines.filter((l) => !selected.includes(l));

  return (
    <div>
      <div className="surface-50 border-1 surface-border border-round p-3 mb-3 text-sm line-height-3">
        <p className="mt-0 mb-2 text-700">
          A <strong>service line</strong> is a group of services the contract
          prices as a unit &mdash; &ldquo;Imaging&rdquo;, &ldquo;Physical
          Therapy&rdquo;, &ldquo;Cardiology&rdquo;. Each card below lists the
          recommendations that apply to that line.
        </p>
        <p className="mt-0 mb-2 text-700">
          A line does <strong>not</strong> necessarily get its own base rate
          file. Most are priced by the contract&rsquo;s main fee schedule, and
          the only thing specific to the line is an adjustment &mdash; a
          multiple-procedure discount, say. So a line showing <em>only</em> a
          &ldquo;Multiple-Procedure Discount&rdquo; is not missing anything: it
          means the contract prices it off the main schedule and just discounts
          repeat procedures. A line that <em>does</em> need its own rates will
          show a Base Rate File and a Rate Calculation.
        </p>
        <p className="mt-0 mb-0 text-700">
          Untick a line to skip it &mdash; its recommendations are rejected, so
          nobody is ever told to build them, and it drops out of the rest of the
          review. You can tick it back on at any time.
        </p>
      </div>

      <p className="text-700 mt-0">
        The agent found {lines.length} service{" "}
        {lines.length === 1 ? "line" : "lines"} in this contract.
      </p>

      <div className="grid">
        {lines.map((line) => {
          const inLine = recsFor(line);
          const isSelected = selected.includes(line);
          return (
            <div className="col-12 md:col-6" key={line}>
              {/*
                The whole card is ONE click target. The earlier version nested a <Checkbox> (with its
                own onChange) inside a <button> (with onClick), so a click on the checkbox fired BOTH
                handlers — two toggles, net zero — and the line could never be unticked. It was also
                invalid HTML: a <button> may not contain an interactive control.

                Now: a div with a single onClick, and the checkbox is presentational (readOnly). One
                click anywhere on the card = exactly one toggle.
              */}
              <div
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={busy ? -1 : 0}
                onClick={() => !busy && onToggle(line)}
                onKeyDown={(e) => {
                  if (busy) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggle(line);
                  }
                }}
                className={`w-full surface-card border-round p-3 border-1 ${
                  busy ? "" : "cursor-pointer"
                } ${
                  isSelected
                    ? "border-primary"
                    : "border-transparent surface-border"
                }`}
              >
                <div className="flex align-items-center gap-2">
                  <Checkbox checked={isSelected} readOnly disabled={busy} />
                  <span className="font-medium">{line}</span>
                </div>
                <div className="text-sm text-700 mt-2 ml-5">
                  {Array.from(
                    new Set(
                      inLine.map((r) => componentTypeLabel(r.componentType))
                    )
                  ).join(", ")}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex align-items-center justify-content-between mt-3">
        <small className="text-700">
          {skipped.length === 0
            ? "Modeling every service line."
            : `Skipping ${skipped.length}: ${skipped.join(
                ", "
              )}. Their recommendations will be rejected.`}
        </small>
        <Button
          label={busy ? "Applying…" : "Apply selection"}
          icon="pi pi-check"
          disabled={busy}
          onClick={onApply}
        />
      </div>
    </div>
  );
}

export default ServiceLinePicker;
